import React, { createContext, useContext, useCallback } from 'react';
import { useNavigate, generatePath, useParams } from 'react-router-dom';
import { useSettings } from './SettingsContext';

interface ThreadContextValue {
  currentProjectId: string | undefined;
  currentThreadId: string | undefined;
  currentVideoId: string | undefined;
  selectProjectIdAndThreadId: (projectId: string | null, threadId: string | null) => void;
  onThreadSelect: (projectId: string, threadId: string) => void;
}

const ThreadContext = createContext<ThreadContextValue | null>(null);

export const useThread = (): ThreadContextValue => {
  const ctx = useContext(ThreadContext);
  if (!ctx) throw new Error('useThread must be used within a ThreadProvider');
  return ctx;
};

export const ThreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { project_id: currentProjectId, thread_id: currentThreadId, video_id: currentVideoId } = useParams<{
    project_id?: string;
    thread_id?: string;
    video_id?: string;
  }>();
  const { setIsExpanded } = useSettings();

  const selectProjectIdAndThreadId = useCallback(
    (projectId: string | null, threadId: string | null) => {
      if (projectId && threadId) {
        navigate(
          generatePath("/projects/:project_id/threads/:thread_id", {
            project_id: projectId,
            thread_id: threadId,
          }),
          { replace: true }
        );
      } else if (projectId) {
        navigate(generatePath("/projects/:project_id", { project_id: projectId }), {
          replace: true,
        });
      } else {
        navigate("/", { replace: true });
      }
    },
    [navigate]
  );

  const onThreadSelect = useCallback(async (projectId: string, threadId: string) => {
    selectProjectIdAndThreadId(projectId, threadId);
    setIsExpanded(false);
  }, [selectProjectIdAndThreadId, setIsExpanded]);

  return (
    <ThreadContext.Provider value={{
      currentProjectId,
      currentThreadId,
      currentVideoId,
      selectProjectIdAndThreadId,
      onThreadSelect,
    }}>
      {children}
    </ThreadContext.Provider>
  );
};
